import React, { useEffect, useState } from 'react';
import { useApp } from '../App';
import { apiRequest } from '../api';

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

export default function WebsiteInboxPage() {
  const { authToken, currentUser } = useApp();
  const [chatRequests, setChatRequests] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [respondingId, setRespondingId] = useState('');
  const [responseNoteById, setResponseNoteById] = useState({});
  const [query, setQuery] = useState('');
  const [chatFilter, setChatFilter] = useState('all');
  const [contactFilter, setContactFilter] = useState('all');

  const role = (currentUser?.role || '').toLowerCase();
  const canViewInbox = ['owner', 'admin', 'webmaster_admin', 'superadmin'].includes(role);

  function passesFilter(item, filter) {
    return filter === 'all' ? true : item.status === filter;
  }

  function matchesQuery(item) {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [item.name, item.email, item.company, item.message, item.subject, item.assigned_to_email]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q);
  }

  useEffect(() => {
    let cancelled = false;

    async function loadInbox(showLoader) {
      if (!authToken || !canViewInbox) {
        setLoading(false);
        return;
      }
      if (showLoader) {
        setLoading(true);
      }
      setError('');
      try {
        const [chat, contact] = await Promise.all([
          apiRequest('/api/v1/website/admin/chat-requests', {}, authToken),
          apiRequest('/api/v1/website/admin/contact-messages', {}, authToken),
        ]);
        if (cancelled) return;
        setChatRequests(chat || []);
        setContactMessages(contact || []);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load website inbox.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadInbox(true);
    const timer = setInterval(() => {
      loadInbox(false);
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [authToken, canViewInbox]);

  async function markResponded(kind, id) {
    setRespondingId(id);
    setError('');
    try {
      const note = (responseNoteById[id] || '').trim();
      const basePath = kind === 'chat' ? 'chat-requests' : 'contact-messages';
      await apiRequest(
        `/api/v1/website/admin/${basePath}/${id}/respond`,
        {
          method: 'PATCH',
          body: JSON.stringify({ response_message: note || null }),
        },
        authToken,
      );
      const [chat, contact] = await Promise.all([
        apiRequest('/api/v1/website/admin/chat-requests', {}, authToken),
        apiRequest('/api/v1/website/admin/contact-messages', {}, authToken),
      ]);
      setChatRequests(chat || []);
      setContactMessages(contact || []);
    } catch (err) {
      setError(err.message || 'Could not mark response.');
    } finally {
      setRespondingId('');
    }
  }

  async function takeOwnership(kind, id) {
    setRespondingId(id);
    setError('');
    try {
      const basePath = kind === 'chat' ? 'chat-requests' : 'contact-messages';
      await apiRequest(`/api/v1/website/admin/${basePath}/${id}/assign-self`, { method: 'PATCH' }, authToken);
      const [chat, contact] = await Promise.all([
        apiRequest('/api/v1/website/admin/chat-requests', {}, authToken),
        apiRequest('/api/v1/website/admin/contact-messages', {}, authToken),
      ]);
      setChatRequests(chat || []);
      setContactMessages(contact || []);
    } catch (err) {
      setError(err.message || 'Could not assign owner.');
    } finally {
      setRespondingId('');
    }
  }

  async function updateStatus(kind, id, nextStatus) {
    setRespondingId(id);
    setError('');
    try {
      const basePath = kind === 'chat' ? 'chat-requests' : 'contact-messages';
      await apiRequest(
        `/api/v1/website/admin/${basePath}/${id}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status: nextStatus }),
        },
        authToken,
      );
      const [chat, contact] = await Promise.all([
        apiRequest('/api/v1/website/admin/chat-requests', {}, authToken),
        apiRequest('/api/v1/website/admin/contact-messages', {}, authToken),
      ]);
      setChatRequests(chat || []);
      setContactMessages(contact || []);
    } catch (err) {
      setError(err.message || 'Could not update status.');
    } finally {
      setRespondingId('');
    }
  }

  const visibleChatRequests = chatRequests.filter((item) => passesFilter(item, chatFilter) && matchesQuery(item));
  const visibleContactMessages = contactMessages.filter((item) => passesFilter(item, contactFilter) && matchesQuery(item));

  return (
    <div className="page-view">
      <div className="page-header">
        <p className="feature-eyebrow">Website Inbox</p>
        <h2>Chat and Contact Requests</h2>
        <p className="page-subtitle">Review incoming website leads from floating chat and contact form.</p>
      </div>

      <div className="workspace-block">
        <div className="form-actions">
          <input
            className="input-sm"
            type="text"
            placeholder="Search by name, email, company, message"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="form-hint">Auto refresh: 3s</span>
        </div>
      </div>

      {!canViewInbox && (
        <p className="auth-feedback auth-error">You do not have permission to access website inbox.</p>
      )}

      {error && <p className="auth-feedback auth-error">{error}</p>}
      {loading && canViewInbox && <p className="form-hint">Loading requests...</p>}

      {!loading && canViewInbox && (
        <div className="analytics-grid website-inbox-grid">
          <section className="workspace-block">
            <h4>Chat Requests ({chatRequests.length})</h4>
            <div className="form-actions">
              <button className={chatFilter === 'all' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setChatFilter('all')}>All</button>
              <button className={chatFilter === 'new' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setChatFilter('new')}>New</button>
              <button className={chatFilter === 'in_progress' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setChatFilter('in_progress')}>In Progress</button>
              <button className={chatFilter === 'responded' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setChatFilter('responded')}>Responded</button>
              <button className={chatFilter === 'resolved' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setChatFilter('resolved')}>Resolved</button>
            </div>
            <ul className="card-list compact-list">
              {visibleChatRequests.map((item) => (
                <li key={item.id}>
                  <div className="account-row">
                    <div>
                      <strong>{item.name}</strong>
                      <span className="account-hint">{item.email} {item.company ? `| ${item.company}` : ''}</span>
                      {item.assigned_to_email && <span className="account-hint">Owner: {item.assigned_to_email}</span>}
                      <p className="form-hint">{item.message}</p>
                      {item.response_message && <p className="form-hint"><strong>Response:</strong> {item.response_message}</p>}
                      {item.responded_by_email && <span className="account-hint">Responded by {item.responded_by_email}</span>}
                      <span className="account-hint">{formatDate(item.created_at)}</span>
                      {item.responded_at && <span className="account-hint">Responded at {formatDate(item.responded_at)}</span>}
                    </div>
                    <div className="account-actions">
                      <select className="input-sm" value={item.status} onChange={(e) => updateStatus('chat', item.id, e.target.value)} disabled={respondingId === item.id}>
                        <option value="new">new</option>
                        <option value="in_progress">in_progress</option>
                        <option value="responded">responded</option>
                        <option value="resolved">resolved</option>
                      </select>
                      {!item.assigned_to_email && (
                        <button className="secondary-action inline-action" type="button" disabled={respondingId === item.id} onClick={() => takeOwnership('chat', item.id)}>
                          {respondingId === item.id ? 'Saving...' : 'Take ownership'}
                        </button>
                      )}
                      {item.status !== 'responded' && (
                        <>
                          <input
                            className="input-sm"
                            type="text"
                            placeholder="Optional response note"
                            value={responseNoteById[item.id] || ''}
                            onChange={(e) => setResponseNoteById((prev) => ({ ...prev, [item.id]: e.target.value }))}
                          />
                          <button
                            className="secondary-action inline-action"
                            type="button"
                            disabled={respondingId === item.id}
                            onClick={() => markResponded('chat', item.id)}
                          >
                            {respondingId === item.id ? 'Saving...' : 'Reply + mark responded'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
              {visibleChatRequests.length === 0 && <li><p className="form-hint">No chat requests for this filter.</p></li>}
            </ul>
          </section>

          <section className="workspace-block">
            <h4>Contact Messages ({contactMessages.length})</h4>
            <div className="form-actions">
              <button className={contactFilter === 'all' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setContactFilter('all')}>All</button>
              <button className={contactFilter === 'new' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setContactFilter('new')}>New</button>
              <button className={contactFilter === 'in_progress' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setContactFilter('in_progress')}>In Progress</button>
              <button className={contactFilter === 'responded' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setContactFilter('responded')}>Responded</button>
              <button className={contactFilter === 'resolved' ? 'secondary-action inline-action' : 'secondary-action inline-action ghost'} type="button" onClick={() => setContactFilter('resolved')}>Resolved</button>
            </div>
            <ul className="card-list compact-list">
              {visibleContactMessages.map((item) => (
                <li key={item.id}>
                  <div className="account-row">
                    <div>
                      <strong>{item.name}</strong>
                      <span className="account-hint">{item.email} {item.company ? `| ${item.company}` : ''}</span>
                      {item.assigned_to_email && <span className="account-hint">Owner: {item.assigned_to_email}</span>}
                      {item.subject && <p className="form-hint"><strong>Subject:</strong> {item.subject}</p>}
                      <p className="form-hint">{item.message}</p>
                      {item.response_message && <p className="form-hint"><strong>Response:</strong> {item.response_message}</p>}
                      {item.responded_by_email && <span className="account-hint">Responded by {item.responded_by_email}</span>}
                      <span className="account-hint">{formatDate(item.created_at)}</span>
                      {item.responded_at && <span className="account-hint">Responded at {formatDate(item.responded_at)}</span>}
                    </div>
                    <div className="account-actions">
                      <select className="input-sm" value={item.status} onChange={(e) => updateStatus('contact', item.id, e.target.value)} disabled={respondingId === item.id}>
                        <option value="new">new</option>
                        <option value="in_progress">in_progress</option>
                        <option value="responded">responded</option>
                        <option value="resolved">resolved</option>
                      </select>
                      {!item.assigned_to_email && (
                        <button className="secondary-action inline-action" type="button" disabled={respondingId === item.id} onClick={() => takeOwnership('contact', item.id)}>
                          {respondingId === item.id ? 'Saving...' : 'Take ownership'}
                        </button>
                      )}
                      {item.status !== 'responded' && (
                        <>
                          <input
                            className="input-sm"
                            type="text"
                            placeholder="Optional response note"
                            value={responseNoteById[item.id] || ''}
                            onChange={(e) => setResponseNoteById((prev) => ({ ...prev, [item.id]: e.target.value }))}
                          />
                          <button
                            className="secondary-action inline-action"
                            type="button"
                            disabled={respondingId === item.id}
                            onClick={() => markResponded('contact', item.id)}
                          >
                            {respondingId === item.id ? 'Saving...' : 'Reply + mark responded'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
              {visibleContactMessages.length === 0 && <li><p className="form-hint">No contact messages for this filter.</p></li>}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
